<script setup lang="ts">
  import { debounce } from "@krainovsd/js-helpers";
  import { VButton, VCheckBox, VInput } from "@krainovsd/vue-ui";
  import dayjs from "dayjs";
  import { computed, ref, useTemplateRef, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { CACHE_NAMESPACE, CACHE_TTL, useCachedResource } from "@/entities/cache";
  import { fetchTrafficProviders } from "@/entities/providers";
  import type { IProvider } from "@/entities/providers";
  import {
    getCustomPeriodRange,
    isReportSubstringDisabled,
    useReportStore,
  } from "@/entities/report";
  import {
    ATTRIBUTION_OPTIONS,
    PERIOD_OPTIONS,
    REPORT_STRING_GROUPS,
    REPORT_STRING_OPTION_BY_KEY,
  } from "@/entities/report/report.constants";
  import Card from "@/components/atoms/Card.vue";
  import Sheet from "@/components/atoms/Sheet.vue";
  import VirtualList from "@/components/atoms/VirtualList.vue";
  import Search from "@/components/icons/Search.vue";
  import Calendar, { type CalendarRange } from "@/components/molecules/Calendar.vue";
  import ChipButton from "@/components/molecules/ChipButton.vue";
  import PickerButton from "@/components/molecules/PickerButton.vue";
  import { sortSelectedFirst } from "@/lib/sort-selected-first";
  import FilterSheet from "./FilterSheet.vue";

  const PROVIDER_ITEM_HEIGHT = 44;
  const SEARCH_DEBOUNCE_MS = 250;

  const { t } = useI18n();
  const reportStore = useReportStore();
  const filters = computed(() => reportStore.currentFilters);

  const searchDraft = ref(filters.value.providerSearch);

  const providersListRef = useTemplateRef<{
    scrollToOffset: (offset: number) => void;
  } | null>("providersListRef");

  const applySearch = debounce((value: string) => {
    filters.value.providerSearch = value;
    providersListRef.value?.scrollToOffset(0);
  }, SEARCH_DEBOUNCE_MS);

  watch(searchDraft, (value) => applySearch(value));

  watch(
    () => filters.value.providerSearch,
    (value) => {
      if (value !== searchDraft.value) searchDraft.value = value;
    },
  );

  const { data: providersData } = useCachedResource<IProvider[]>({
    namespace: CACHE_NAMESPACE.TrafficProviders,
    key: "all",
    ttl: CACHE_TTL.Long,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchTrafficProviders(),
  });

  function providerKey(provider: IProvider) {
    return provider.id;
  }

  const periods = computed(() =>
    PERIOD_OPTIONS.map((option) => ({
      ...option,
      label: t(option.label.path, {}, { default: option.label.default }),
    })),
  );

  const selectedPeriodLabel = computed(() => {
    if (filters.value.period === "fixPeriod" && filters.value.dateFrom && filters.value.dateTo) {
      const from = dayjs(filters.value.dateFrom).format("DD.MM.YYYY");
      const to = dayjs(filters.value.dateTo).format("DD.MM.YYYY");
      return `${from} — ${to}`;
    }
    return periods.value.find((option) => option.key === filters.value.period)?.label ?? null;
  });

  const fixPeriodSheetOpen = ref(false);
  const draftRange = ref<CalendarRange>({ start: null, end: null });

  const canApply = computed(() => draftRange.value.start !== null && draftRange.value.end !== null);

  function openFixPeriod() {
    // Seed the calendar from the currently applied fixed range, if any.
    const { period, dateFrom, dateTo } = filters.value;
    const isFixed = period === "fixPeriod";
    draftRange.value = {
      start: isFixed && dateFrom ? dayjs(dateFrom).toDate() : null,
      end: isFixed && dateTo ? dayjs(dateTo).toDate() : null,
    };
    fixPeriodSheetOpen.value = true;
  }

  function selectPeriod(key: number | "fixPeriod") {
    if (key === "fixPeriod") {
      openFixPeriod();
      return;
    }
    // A dynamic preset supersedes any fixed range.
    filters.value.period = key;
    filters.value.dateFrom = null;
    filters.value.dateTo = null;
  }

  function applyFixPeriod() {
    const { start, end } = draftRange.value;
    if (!start || !end) return;
    const period = getCustomPeriodRange(start, end);
    filters.value.dateFrom = period.dateFrom;
    filters.value.dateTo = period.dateTo;
    filters.value.period = "fixPeriod";
    fixPeriodSheetOpen.value = false;
  }

  // "Строки" (section) and "Подстроки" (groupByNested) share one grouped option
  // set, mirroring the React report form.
  const stringGroups = computed(() =>
    REPORT_STRING_GROUPS.map((group) => ({
      title: t(group.title.path, {}, { default: group.title.default }),
      options: group.options.map((option) => ({
        key: option.key,
        label: t(option.title.path, {}, { default: option.title.default }),
      })),
    })),
  );

  function stringOptionLabel(key: number | null) {
    if (key === null) return null;
    const option = REPORT_STRING_OPTION_BY_KEY[key];
    return option ? t(option.title.path, {}, { default: option.title.default }) : null;
  }

  const selectedSectionLabel = computed(() => stringOptionLabel(filters.value.section));

  const selectedBreakdownLabel = computed(() => stringOptionLabel(filters.value.groupByNested));

  // A substring is disabled when the chosen section forbids it (date hierarchy /
  // triggers) or when it would repeat the section.
  function isBreakdownDisabled(key: number) {
    return isReportSubstringDisabled(filters.value.section, key) || filters.value.section === key;
  }

  function selectSection(key: number) {
    filters.value.section = key;
    // Drop a substring that the new section now forbids, so the applied filter
    // can never hold an invalid section/substring pair.
    if (filters.value.groupByNested !== null && isBreakdownDisabled(filters.value.groupByNested)) {
      filters.value.groupByNested = null;
    }
  }

  function selectBreakdown(key: number | null) {
    if (key !== null && isBreakdownDisabled(key)) return;
    filters.value.groupByNested = key;
  }

  const providerOptions = computed(() => providersData.value ?? []);

  const filteredProviders = computed(() => {
    const query = filters.value.providerSearch.trim().toLowerCase();
    const list = query
      ? providerOptions.value.filter((option) => option.name.toLowerCase().includes(query))
      : providerOptions.value;
    // Surface the currently-checked providers at the top of the list.
    return sortSelectedFirst(list, providersDraft.value);
  });

  // The provider selection is edited in a draft and only written to the applied
  // filters on "Применить", so the report request fires once — on confirm —
  // instead of on every checkbox toggle.
  const providersSheetOpen = ref(false);
  const providersDraft = ref<number[]>([...filters.value.providers]);

  watch(providersSheetOpen, (open) => {
    // Re-seed from the applied selection on each open, discarding edits that
    // were left unapplied when the sheet was previously closed.
    if (open) providersDraft.value = [...filters.value.providers];
  });

  const selectedProvidersLabel = computed(() => {
    return filters.value.providers?.length ? `${filters.value.providers.length} выбрано` : null;
  });

  const providersChanged = computed(() => {
    const applied = filters.value.providers;
    if (applied.length !== providersDraft.value.length) return true;
    const appliedSet = new Set(applied);
    return providersDraft.value.some((id) => !appliedSet.has(id));
  });

  function toggleProvider(key: number, checked: boolean) {
    const set = new Set(providersDraft.value);
    if (checked) set.add(key);
    else set.delete(key);
    providersDraft.value = Array.from(set);
  }

  function applyProviders() {
    filters.value.providers = [...providersDraft.value];
    providersSheetOpen.value = false;
  }

  const attributions = computed(() =>
    ATTRIBUTION_OPTIONS.map((option) => ({
      ...option,
      title: t(option.title.path, {}, { default: option.title.default }),
      subtitle: t(option.subtitle.path, {}, { default: option.subtitle.default }),
      description: t(option.description.path, {}, { default: option.description.default }),
    })),
  );

  const selectedAttributionTitle = computed(
    () =>
      attributions.value.find((option) => option.key === filters.value.attribution)?.title ?? null,
  );

  function selectAttribution(key: number) {
    filters.value.attribution = key;
  }

  // const ATTRIBUTION_TRIGGER_OPTIONS = [
  //   {
  //     key: "primary",
  //     title: t("page.report.attributionTrigger.primary.title", {}, { default: "Один первичный" }),
  //     subtitle: t(
  //       "page.report.attributionTrigger.primary.subtitle",
  //       {},
  //       { default: "Засчитываем только первый триггер на пользователя." },
  //     ),
  //   },
  //   {
  //     key: "fiveMinutes",
  //     title: t(
  //       "page.report.attributionTrigger.fiveMinutes.title",
  //       {},
  //       { default: "Все за 5 минут" },
  //     ),
  //     subtitle: t(
  //       "page.report.attributionTrigger.fiveMinutes.subtitle",
  //       {},
  //       { default: "Все триггеры в окне 5 минут после конверсии." },
  //     ),
  //   },
  // ];

  // const selectedAttributionTriggerTitle = computed(
  //   () =>
  //     ATTRIBUTION_TRIGGER_OPTIONS.find((option) => option.key === filters.value.attributionTrigger)
  //       ?.title ?? null,
  // );

  // function selectAttributionTrigger(key: string) {
  //   filters.value.attributionTrigger = key;
  // }
</script>

<template>
  <section :class="$style.filters">
    <PickerButton
      :value="selectedPeriodLabel"
      :placeholder="$t('page.report.period.placeholder', {}, { default: 'Период' })"
      :title="$t('page.report.period.title', {}, { default: 'Период' })"
      mode="sheet"
    >
      <div :class="$style.periodChips">
        <ChipButton
          v-for="option in periods"
          :key="option.key"
          :value="option.label"
          :placeholder="option.label"
          :active="filters.period === option.key"
          @click="selectPeriod(option.key as number | 'fixPeriod')"
        />
      </div>
    </PickerButton>
    <Sheet
      v-model:open="fixPeriodSheetOpen"
      :title="$t('page.report.period.customTitle', {}, { default: 'Фиксированный период' })"
    >
      <div :class="$style.fixedPeriod">
        <Calendar v-model:range="draftRange" mode="range" locale="ru-RU" />
        <div :class="$style.fixedPeriodFooter">
          <VButton type="primary" :disabled="!canApply" @click="applyFixPeriod">
            {{ $t("page.report.period.apply", {}, { default: "Применить" }) }}
          </VButton>
        </div>
      </div>
    </Sheet>
    <PickerButton
      :value="selectedSectionLabel"
      :placeholder="$t('page.report.section.placeholder', {}, { default: 'Разрез' })"
      :title="$t('page.report.section.title', {}, { default: 'Разрез' })"
      mode="sheet"
    >
      <div :class="$style.breakdownGroups">
        <div v-for="group in stringGroups" :key="group.title" :class="$style.breakdownGroup">
          <span :class="$style.breakdownGroupLabel">{{ group.title }}</span>
          <ul :class="$style.breakdownList">
            <li
              v-for="option in group.options"
              :key="option.key"
              :class="$style.breakdownItem"
              @click="selectSection(option.key)"
            >
              <span>{{ option.label }}</span>
              <span
                v-if="filters.section === option.key"
                :class="$style.activeDot"
                aria-hidden="true"
              />
            </li>
          </ul>
        </div>
      </div>
    </PickerButton>
    <PickerButton
      :value="selectedBreakdownLabel"
      :placeholder="$t('page.report.groupBy.placeholder', {}, { default: 'Разбивка по' })"
      :title="$t('page.report.groupBy.title', {}, { default: 'Разбивка по' })"
      mode="sheet"
    >
      <div :class="$style.breakdownGroups">
        <ul :class="$style.breakdownList">
          <li :class="$style.breakdownItem" @click="selectBreakdown(null)">
            <span>{{
              $t("page.createReport.groupBy.without.title", {}, { default: "Без разбивки" })
            }}</span>
            <span
              v-if="filters.groupByNested === null"
              :class="$style.activeDot"
              aria-hidden="true"
            />
          </li>
        </ul>
        <div v-for="group in stringGroups" :key="group.title" :class="$style.breakdownGroup">
          <span :class="$style.breakdownGroupLabel">{{ group.title }}</span>
          <ul :class="$style.breakdownList">
            <li
              v-for="option in group.options"
              :key="option.key"
              :class="[
                $style.breakdownItem,
                isBreakdownDisabled(option.key) && $style.breakdownItemDisabled,
              ]"
              @click="selectBreakdown(option.key)"
            >
              <span>{{ option.label }}</span>
              <span
                v-if="filters.groupByNested === option.key"
                :class="$style.activeDot"
                aria-hidden="true"
              />
            </li>
          </ul>
        </div>
      </div>
    </PickerButton>
    <PickerButton
      v-model:open="providersSheetOpen"
      :value="selectedProvidersLabel"
      :placeholder="$t('page.report.providers.placeholder', {}, { default: 'Все' })"
      :title="$t('page.report.providers.title', {}, { default: 'Поставщики' })"
      mode="sheet"
    >
      <div :class="$style.providers">
        <VInput
          v-model="searchDraft"
          shape="round"
          size="large"
          :placeholder="$t('page.report.providers.searchPlaceholder', {}, { default: 'Поиск' })"
        >
          <template #prefix>
            <Search />
          </template>
        </VInput>
        <VirtualList
          ref="providersListRef"
          :class="$style.providersList"
          :items="filteredProviders"
          :item-size="PROVIDER_ITEM_HEIGHT"
        >
          <template #default="{ item }">
            <div :class="$style.providersItem">
              <VCheckBox
                :model-value="providersDraft.includes(providerKey(item))"
                @update:model-value="
                  (checked) => toggleProvider(providerKey(item), checked ?? false)
                "
              >
                {{ item.name }}
              </VCheckBox>
            </div>
          </template>
        </VirtualList>
        <div :class="$style.providersFooter">
          <VButton type="primary" :disabled="!providersChanged" @click="applyProviders">
            {{ $t("page.report.providers.apply", {}, { default: "Применить" }) }}
          </VButton>
        </div>
      </div>
    </PickerButton>
    <FilterSheet />
    <PickerButton
      :value="selectedAttributionTitle"
      :placeholder="$t('page.report.attribution.placeholder', {}, { default: 'Режим атрибуции' })"
      :title="$t('page.report.attribution.title', {}, { default: 'Режим атрибуции' })"
      mode="sheet"
    >
      <div :class="$style.attribution">
        <Card
          v-for="option in attributions"
          :key="option.key"
          :class="[
            $style.attributionCard,
            { [$style.attributionCardActive]: filters.attribution === option.key },
          ]"
          @click="selectAttribution(option.key)"
        >
          <div :class="$style.attributionTitle">{{ option.title }}</div>
          <div :class="$style.attributionSubtitle">{{ option.description }}</div>
        </Card>
      </div>
    </PickerButton>
    <!-- <PickerButton
      :value="selectedAttributionTriggerTitle"
      :placeholder="
        $t('page.report.attributionTrigger.placeholder', {}, { default: 'Атрибуция триггера' })
      "
      :title="$t('page.report.attributionTrigger.title', {}, { default: 'Атрибуция триггера' })"
      mode="sheet"
    >
      <div :class="$style.attribution">
        <Card
          v-for="option in ATTRIBUTION_TRIGGER_OPTIONS"
          :key="option.key"
          :class="[
            $style.attributionCard,
            { [$style.attributionCardActive]: filters.attributionTrigger === option.key },
          ]"
          @click="selectAttributionTrigger(option.key)"
        >
          <div :class="$style.attributionTitle">{{ option.title }}</div>
          <div :class="$style.attributionSubtitle">{{ option.subtitle }}</div>
        </Card>
      </div>
    </PickerButton> -->
  </section>
</template>

<style lang="scss" module>
  .filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .periodChips {
    width: 100%;

    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .fixedPeriod {
    width: 100%;
    max-width: 20rem;
    margin: 0 auto;

    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .fixedPeriodFooter {
    display: flex;
    justify-content: flex-end;
  }

  .breakdownGroups {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .breakdownGroup {
    display: flex;
    flex-direction: column;
  }

  .breakdownGroupLabel {
    padding: 4px 8px;

    color: var(--muted-foreground);
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .breakdownList {
    list-style: none;
    margin: 0;
    padding: 0;

    display: flex;
    flex-direction: column;
  }

  .breakdownItem {
    padding: 12px 8px;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    color: var(--foreground);
    cursor: pointer;
  }

  .breakdownItemDisabled {
    color: var(--muted-foreground);
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .activeDot {
    width: 10px;
    height: 10px;

    border-radius: 50%;
    background-color: var(--brand);
  }

  .providers {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .providersList {
    height: 400px;
  }

  .providersFooter {
    display: flex;
    justify-content: flex-end;
  }

  .providersItem {
    height: 44px;

    padding: 10px 8px;

    display: flex;
    align-items: center;
  }

  .attribution {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .attributionCard {
    display: flex;
    flex-direction: column;
    gap: 4px;

    cursor: pointer;
  }

  .attributionCardActive {
    outline: 1px solid var(--brand);
    outline-offset: -1px;
  }

  .attributionTitle {
    font-weight: 600;
    font-size: 1rem;
    line-height: 1.5rem;

    color: var(--foreground);
  }

  .attributionSubtitle {
    font-size: 0.875rem;
    line-height: 1.25rem;

    color: var(--muted-foreground);
  }
</style>
